import { FC, useEffect, useMemo, useState } from 'react';

import { Link, router, usePage } from '@inertiajs/react';
import { BarChart3, Plus } from 'lucide-react';

import {
    clearProfileOffersReturnContext,
    ProfileOffersReturnContext,
    readProfileOffersReturnContext,
    replaceCurrentProfileOffersPath,
    restoreProfileOffersReturnContext,
} from './__shared/returnContext';
import { ProfileDesktopFilters } from './filters/DesktopFilters';
import { ProfileMobileFilters } from './filters/MobileFilters';
import { ActionsRow } from './Index/ActionsRow';
import { AnalyticsRow } from './Index/AnalyticsRow';
import { EmptyState } from './Index/EmptyState';
import { Offers } from './Index/Offers';
import { Pagination } from './Index/Pagination';
import { PremiumDetailsDialog } from './Premium/PremiumDetailsDialog';
import { PremiumServicesShortcut } from './PremiumServicesShortcut';
import { Button } from '../../../components/ui/Button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../components/ui/Select';
import { Routes } from '../../../models/routes';
import { LocalStorageService } from '../../../services/LocalStorageService';
import {
    Expansion,
    Offer,
    OfferProperty,
    OfferType,
    ActivePremiumService,
    PremiumPackage,
    PremiumSummary,
    ProductFilter,
    ProductType,
    Universe,
} from '../../../types';
import { LocalStorageKeys } from '../../../types/localStorage';
import { PropertyOption } from '../../../types/shared';
import { ConfirmModalState } from '../../../types/ui';

type PageProps = {
    initialOffers: Offer[] | null;
    offersCounts: Record<OfferType, number>;
    offerProperties: OfferProperty[];
    nextPage: number | null;
    q: Record<string, string> | null;
    sort: string;
    currentProductType: ProductType | null;
    currentExpansion: Expansion | null;
    expansions: Expansion[] | null;
    filtersConfig: Record<string, ProductFilter[]>;
    filterUniverses: Universe[] | null;
    filterProductTypes: ProductType[] | null;
    premiumSummary: PremiumSummary;
    premiumPackages: PremiumPackage[] | null;
    activePremiumServices: ActivePremiumService[] | null;
};

const RESERVED_QUERY_KEYS = new Set(['search', 'status', 'expansion_slug']);

const getStatusFromQuery = (status?: string): OfferType =>
    status === 'inactive' ? 'inactive' : status === 'all' ? 'all' : 'active';

const extractOfferFilters = (query: Record<string, string> | null) =>
    Object.fromEntries(
        Object.entries(query ?? {}).filter(
            ([key]) => !RESERVED_QUERY_KEYS.has(key),
        ),
    );

const removeBlankFilters = (filters: Record<string, string>) =>
    Object.fromEntries(
        Object.entries(filters).filter(
            ([, value]) => Boolean(value) && value !== 'all',
        ),
    );

const stripPropertyFilters = (filters: Record<string, string>) =>
    Object.fromEntries(
        Object.entries(filters).filter(([key]) =>
            ['universe_slug', 'product_type_slug'].includes(key),
        ),
    );

const shouldOpenPremiumDialog = (url: string) =>
    new URLSearchParams(url.split('?')[1] ?? '').get('premium') === 'open';

const removePremiumDialogQuery = () => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete('premium');
    window.history.replaceState(
        window.history.state,
        '',
        `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`,
    );
};

const buildOffersQuery = ({
    search,
    status,
    filters,
    currentFilters,
    expansionSlug,
    currentExpansionSlug,
}: {
    search: string;
    status: OfferType;
    filters: Record<string, string>;
    currentFilters: Record<string, string>;
    expansionSlug?: string;
    currentExpansionSlug?: string;
}) => {
    let sanitizedFilters = removeBlankFilters(filters);
    const universeChanged =
        currentFilters.universe_slug !== sanitizedFilters.universe_slug;
    const productTypeChanged =
        currentFilters.product_type_slug !== sanitizedFilters.product_type_slug;

    if (!sanitizedFilters.universe_slug) {
        sanitizedFilters = {};
    } else if (universeChanged) {
        sanitizedFilters = stripPropertyFilters(sanitizedFilters);
        delete sanitizedFilters.product_type_slug;
    } else if (!sanitizedFilters.product_type_slug || productTypeChanged) {
        sanitizedFilters = stripPropertyFilters(sanitizedFilters);
    }

    const nextExpansionSlug =
        universeChanged || !sanitizedFilters.universe_slug
            ? undefined
            : (expansionSlug ?? currentExpansionSlug);

    return removeBlankFilters({
        ...sanitizedFilters,
        ...(search ? { search } : {}),
        status,
        ...(nextExpansionSlug ? { expansion_slug: nextExpansionSlug } : {}),
    });
};

const mapOptions = (
    items: Array<{ id: number; name: string; slug: string }> | null,
): PropertyOption[] =>
    (items ?? []).map((item) => ({
        id: item.id,
        name: item.name.trim(),
        slug: item.slug,
    }));

const Index: FC = () => {
    const page = usePage<PageProps>();
    const {
        initialOffers,
        offersCounts,
        offerProperties,
        nextPage,
        q,
        sort: initialSort,
        currentExpansion,
        expansions,
        filterUniverses,
        filterProductTypes,
        premiumSummary: initialPremiumSummary,
        premiumPackages,
        activePremiumServices,
    } = page.props;

    const [offers, setOffers] = useState<Offer[]>(initialOffers ?? []);
    const [premiumSummary, setPremiumSummary] = useState(initialPremiumSummary);
    const [premiumDialogOpen, setPremiumDialogOpen] = useState(() =>
        shouldOpenPremiumDialog(page.url),
    );
    const [search, setSearch] = useState(q?.search ?? '');
    const [selectedOffers, setSelectedOffers] = useState<number[]>([]);
    const [viewMode, setViewMode] = useState(
        () => LocalStorageService.getItem(LocalStorageKeys.VIEW_MODE) ?? 'list',
    );
    const [confirmModalState, setConfirmModalState] =
        useState<ConfirmModalState>({
            open: false,
            payload: null,
        });
    const [status, setStatus] = useState<OfferType>(
        getStatusFromQuery(q?.status),
    );
    const [sort, setSort] = useState(initialSort || 'newest');
    const [loading, setLoading] = useState(false);
    const [loadedPages, setLoadedPages] = useState(1);
    const [returnContext, setReturnContext] =
        useState<ProfileOffersReturnContext | null>(null);

    const currentFilters = useMemo(() => extractOfferFilters(q), [q]);
    const currentExpansionSlug = currentExpansion?.slug;
    const extraFilters = useMemo(
        () => [
            {
                key: 'universe_slug',
                label: 'Всесвіт',
                allLabel: 'Всесвіти',
                options: mapOptions(filterUniverses),
                searchable: true,
                testId: 'profile-universe-filter',
            },
            {
                key: 'product_type_slug',
                label: 'Тип продукту',
                allLabel: 'Тип продукту',
                options: mapOptions(filterProductTypes),
                searchable: true,
                testId: 'profile-product-type-filter',
            },
        ],
        [filterProductTypes, filterUniverses],
    );
    const showFilters =
        offersCounts.all > 0 ||
        Object.keys(currentFilters).length > 0 ||
        Boolean(currentExpansion);

    const requestOffers = (
        nextFilters: Record<string, string>,
        nextExpansionSlug?: string,
        nextSort = sort,
    ) => {
        setLoadedPages(1);
        router.get(
            Routes.Offers,
            {
                page: 1,
                append: false,
                sort: nextSort,
                q: buildOffersQuery({
                    search,
                    status,
                    filters: nextFilters,
                    currentFilters,
                    expansionSlug: nextExpansionSlug,
                    currentExpansionSlug,
                }),
            },
            {
                preserveScroll: true,
                preserveState: true,
                onStart: () => setLoading(true),
                onSuccess: () => setLoading(false),
            },
        );
    };

    const loadMoreOffers = (page: number) => {
        router.get(
            Routes.Offers,
            {
                page,
                sort,
                q: {
                    ...currentFilters,
                    ...(currentExpansionSlug
                        ? { expansion_slug: currentExpansionSlug }
                        : {}),
                    ...(search ? { search } : {}),
                    status,
                },
                append: true,
            },
            {
                preserveScroll: true,
                preserveState: true,
                preserveUrl: true,
                only: ['initialOffers', 'nextPage', 'q'],
                onStart: () => {
                    setLoading(true);
                },
                onSuccess: () => {
                    setLoadedPages(page);
                },
                onFinish: () => {
                    setLoading(false);
                },
            },
        );
    };

    const handleSortChange = (value: string) => {
        setSort(value);
        requestOffers(currentFilters, currentExpansionSlug, value);
    };

    useEffect(() => {
        setStatus(getStatusFromQuery(q?.status));
    }, [q?.status]);

    useEffect(() => {
        setSearch(q?.search ?? '');
    }, [q?.search]);

    useEffect(() => {
        setSort(initialSort || 'newest');
    }, [initialSort]);

    useEffect(() => {
        setOffers(initialOffers ?? []);
    }, [initialOffers]);

    useEffect(() => {
        setPremiumSummary(initialPremiumSummary);
    }, [initialPremiumSummary]);

    useEffect(() => {
        if (shouldOpenPremiumDialog(page.url)) {
            setPremiumDialogOpen(true);
        }
    }, [page.url]);

    useEffect(() => {
        setReturnContext(readProfileOffersReturnContext());
    }, []);

    useEffect(() => {
        if (!returnContext || loading) {
            return;
        }

        if (loadedPages >= returnContext.page || nextPage === null) {
            clearProfileOffersReturnContext();
            replaceCurrentProfileOffersPath(returnContext.path);
            restoreProfileOffersReturnContext(returnContext.scrollY);
            setReturnContext(null);
            return;
        }

        loadMoreOffers(nextPage);
    }, [loadedPages, loading, nextPage, returnContext]);

    return (
        <div className="py-6">
            <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
                <div className="mb-3 md:mb-6">
                    <h1 className="text-2xl font-bold sm:text-3xl">
                        Пропозиції
                    </h1>
                </div>

                <div className="mb-2 flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:self-center md:mb-0">
                    <Button
                        asChild
                        variant="outline"
                        className="w-full sm:w-auto"
                    >
                        <Link href={Routes.OffersAnalytics}>
                            <BarChart3 className="mr-2 h-4 w-4" />
                            Аналіз цін
                        </Link>
                    </Button>
                    <Button asChild className="w-full sm:w-auto">
                        <Link href={Routes.NewOffer}>
                            <Plus className="mr-2 h-4 w-4" />
                            Створити нову пропозицію
                        </Link>
                    </Button>
                </div>
            </div>

            <AnalyticsRow counts={offersCounts} />

            <PremiumServicesShortcut
                premiumSummary={premiumSummary}
                onManage={() => setPremiumDialogOpen(true)}
            />

            <PremiumDetailsDialog
                open={premiumDialogOpen}
                onOpenChange={(open) => {
                    setPremiumDialogOpen(open);

                    if (!open) {
                        removePremiumDialogQuery();
                    }
                }}
                premiumPackages={premiumPackages}
                activePremiumServices={activePremiumServices}
                premiumSummary={premiumSummary}
            />

            <ActionsRow
                search={search}
                status={status}
                sort={sort}
                filters={currentFilters}
                expansionSlug={currentExpansionSlug}
                setSearch={setSearch}
                setStatus={setStatus}
                viewMode={viewMode}
                setViewMode={setViewMode}
                setConfirmModalState={setConfirmModalState}
                selectedOffers={selectedOffers}
                offers={offers}
                setSelectedOffers={setSelectedOffers}
                offersCounts={offersCounts}
                disabled={loading}
                setLoading={setLoading}
            />

            {showFilters && (
                <>
                    <ProfileDesktopFilters
                        filters={currentFilters}
                        onFilterChange={({ q: nextFilters, expansionSlug }) =>
                            requestOffers(nextFilters, expansionSlug)
                        }
                        onResetFilters={() => requestOffers({})}
                        expansion={currentExpansion}
                        expansions={expansions}
                        extraFilters={extraFilters}
                        hidePropertyFiltersWithoutProductType
                        sticky={false}
                        showBorder={false}
                        sort={sort}
                        onSortChange={handleSortChange}
                        className="-mx-4 mb-2 sm:-mx-6 md:block lg:-mx-8"
                    />

                    <div className="md:hidden">
                        <ProfileMobileFilters
                            filters={currentFilters}
                            onFilterChange={({
                                q: nextFilters,
                                expansionSlug,
                            }) => requestOffers(nextFilters, expansionSlug)}
                            expansion={currentExpansion}
                            extraFilters={extraFilters}
                            hidePropertyFiltersWithoutProductType
                            immediateApplyKeys={[
                                'universe_slug',
                                'product_type_slug',
                            ]}
                        />
                    </div>
                </>
            )}

            {offersCounts.all > 0 && (
                <div className="mb-4 flex flex-col gap-2 md:hidden">
                    <Select
                        value={sort || 'newest'}
                        onValueChange={handleSortChange}
                    >
                        <SelectTrigger
                            className="h-9 w-full text-sm [&_svg]:h-3.5 [&_svg]:w-3.5"
                            data-testid="profile-offers-sort-trigger"
                        >
                            <SelectValue placeholder="Новизною" />
                        </SelectTrigger>
                        <SelectContent className="text-sm">
                            <SelectItem className="py-1 text-sm" value="newest">
                                Новизною
                            </SelectItem>
                            <SelectItem
                                className="py-1 text-sm"
                                value="price-low"
                            >
                                Ціна: від низької до високої
                            </SelectItem>
                            <SelectItem
                                className="py-1 text-sm"
                                value="price-high"
                            >
                                Ціна: від високої до низької
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            )}

            <Offers
                confirmModalState={confirmModalState}
                setConfirmModalState={setConfirmModalState}
                offers={offers}
                offerProperties={offerProperties}
                loadedPages={loadedPages}
                premiumSummary={premiumSummary}
                loading={loading}
                viewMode={viewMode}
                selectedOffers={selectedOffers}
                setSelectedOffers={setSelectedOffers}
                onPremiumActivated={(updatedOffer, updatedPremiumSummary) => {
                    setOffers((currentOffers) =>
                        currentOffers.map((offer) =>
                            offer.id === updatedOffer.id ? updatedOffer : offer,
                        ),
                    );
                    setPremiumSummary(updatedPremiumSummary);
                }}
            />

            <Pagination
                nextPage={nextPage}
                loading={loading}
                onLoadMore={loadMoreOffers}
            />

            {(!offers || offers.length === 0) && <EmptyState />}
        </div>
    );
};

export default Index;
